import { FarmRiskType, lookupsJson, StructureCategory, policyType } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

const { agriculturalHeatingSourceOptions, inlandMarineTypes, inlandMarineTypesByState } = lookupsJson.farm;
const inlandMarineCategoryLabels = Object.fromEntries(
  [...(inlandMarineTypes || []), ...Object.values(inlandMarineTypesByState || {}).flat()].map((t) => [t.id, t.value])
);

// Default: all construction types except Mobile Home
// Reference: Issue #28408 - "Get rid of Mobile Home as a construction type for all FO-6 structure types"
const DEFAULT_CONSTRUCTION_TYPES = (lookupsJson.farm.agriculturalConstructionType || [])
  .map((ct) => ct.id)
  .filter((id) => id !== 'MH');

export const getLocationOptions = (farmLocations) => {
  return (farmLocations || [])
    .filter((loc) => !loc.isDeleted)
    .map((loc) => ({
      id: loc.id,
      value: `Location - ${loc.description}`
    }));
};

export const getEquipmentOptions = (farmLocations) => {
  const items = [];
  for (const loc of farmLocations || []) {
    if (loc.isDeleted) {
      continue;
    }
    const locationItems = [];
    for (const risk of loc.risks || []) {
      if (risk.type !== 'FO6Scheduled') {
        continue;
      }
      for (const item of risk.coverages?.coverageF || []) {
        const categoryLabel = inlandMarineCategoryLabels[item.category] || item.category;
        locationItems.push({
          id: `${loc.id}::${risk.id}::${item.id}`,
          value: `${categoryLabel} - ${item.description || item.category}`
        });
      }
    }
    if (locationItems.length > 0) {
      items.push({ id: `header-${loc.id}`, value: loc.description, isGroupHeader: true });
      items.push(...locationItems);
    }
  }
  return items;
};

const riskTypeLabels = Object.fromEntries((lookupsJson.farm.riskType || []).map((r) => [r.id, r.value]));

// Resolves the display label for a farm risk type, honoring the policy-type mask's
// fo6StructuresLabel override when set (Rental Y / Home Z → "Other Structures (FO-6)"). (#31367)
const getFarmRiskTypeLabel = (riskTypeId, farmPolicyType, state) => {
  if (riskTypeId === FarmRiskType.Fo6Structures && farmPolicyType && state) {
    const mask = getFarmPolicyMask(farmPolicyType, state);
    if (mask?.fo6StructuresLabel) {
      return mask.fo6StructuresLabel;
    }
  }
  return riskTypeLabels[riskTypeId] || riskTypeId;
};

export const getRiskOptions = (farmLocations, farmPolicyType, state) => {
  const items = [];
  for (const loc of farmLocations || []) {
    if (loc.isDeleted) {
      continue;
    }
    for (const risk of loc.risks || []) {
      const label = getFarmRiskTypeLabel(risk.type, farmPolicyType, state);
      items.push({
        id: `${loc.id}::${risk.id}`,
        value: `${label.replace(/^.*?- /, '')} - ${risk.description}`
      });
    }
  }
  return items;
};

export const getSppOptions = (farmLocations) => {
  const items = [];
  for (const loc of farmLocations || []) {
    if (loc.isDeleted) {
      continue;
    }
    const locationItems = [];
    for (const risk of loc.risks || []) {
      for (const sc of risk.coverages?.scheduleCoverages || []) {
        if (sc.coverageType !== 'SPP') {
          continue;
        }
        for (const item of sc.items || []) {
          const categoryLabel = inlandMarineCategoryLabels[item.category] || item.category;
          locationItems.push({
            id: `${loc.id}::${risk.id}::${item.id}`,
            value: `${categoryLabel} - ${item.name || item.description}`
          });
        }
      }
    }
    if (locationItems.length > 0) {
      items.push({ id: `header-${loc.id}`, value: loc.description, isGroupHeader: true });
      items.push(...locationItems);
    }
  }
  return items;
};

export const getRpsOptions = (farmLocations) => {
  const items = [];
  for (const loc of farmLocations || []) {
    if (loc.isDeleted) {
      continue;
    }
    const locationItems = [];
    for (const risk of loc.risks || []) {
      for (const sc of risk.coverages?.scheduleCoverages || []) {
        if (sc.coverageType !== 'RELATED_PRIVATE_STRUCTURES') {
          continue;
        }
        for (const item of sc.items || []) {
          locationItems.push({
            id: `${loc.id}::${risk.id}::${item.id}`,
            value: item.extendedDetails?.name || item.description || item.id
          });
        }
      }
    }
    if (locationItems.length > 0) {
      items.push({ id: `header-${loc.id}`, value: loc.description, isGroupHeader: true });
      items.push(...locationItems);
    }
  }
  return items;
};

/**
 * Get heating options based on building type and occupancy for FO6 structures
 * @param {string} buildingType - The type of the building (e.g., Barns/Outbuildings)
 * @param {string} occupancy - The occupancy type of the building
 * @returns {Array} - Filtered heating options
 */
export const getHeatingOptionsByOccupancy = (buildingType, occupancy) => {
  if (buildingType === StructureCategory.BarnsOutbuildings) {
    if (occupancy === 'MH') {
      return agriculturalHeatingSourceOptions.filter((option) => ['HEAT', 'NONE'].includes(option.id));
    }

    if (occupancy === 'GRNA') {
      return agriculturalHeatingSourceOptions.filter((option) => !['HP', 'FR', 'HW', 'RC', 'RF'].includes(option.id));
    }

    return agriculturalHeatingSourceOptions.filter(
      (option) => !['HP', 'FR', 'EB', 'HW', 'RC', 'RF'].includes(option.id)
    );
  }

  if (occupancy === 'PCH') {
    return agriculturalHeatingSourceOptions.filter((option) => !['HP', 'EB', 'RF'].includes(option.id));
  }

  return agriculturalHeatingSourceOptions;
};

/**
 * Maps dwelling heating option to agricultural heating option for 360Value/Verisk
 * @param {string} dwellingHeatingCode - The dwelling heating system code (FA, HP, etc.)
 * @returns {string} - The corresponding agricultural heating system code for Verisk
 */
export const mapDwellingToAgriculturalHeating = (dwellingHeatingCode) => {
  const mappings = lookupsJson.farm.heatingSystemMappings?.dwellingToAgricultural || {};
  return mappings[dwellingHeatingCode] || dwellingHeatingCode; // fallback to original if no mapping
};

/**
 * Maps agricultural heating option back to dwelling heating option for reverse lookup
 * @param {string} agriculturalHeatingCode - The agricultural heating system code (FR, HP, etc.)
 * @returns {string} - The corresponding dwelling heating system code
 */
export const mapAgriculturalToDwellingHeating = (agriculturalHeatingCode) => {
  const mappings = lookupsJson.farm.heatingSystemMappings?.dwellingToAgricultural || {};
  // Find the dwelling code that maps to this agricultural code
  const reverseEntry = Object.entries(mappings).find(([, agCode]) => agCode === agriculturalHeatingCode);
  return reverseEntry ? reverseEntry[0] : agriculturalHeatingCode; // fallback to original if no mapping
};

/**
 * Gets the normal dwelling heating options (same as FO-1/FO-2/FO-3) for FO-6 underwriting
 * This is what should be displayed in the UI for user selection
 * @returns {Array} - Array of dwelling heating source options
 */
export const getDwellingHeatingOptionsForFO6 = () => {
  return lookupsJson.farm.primaryHeatingSourceOptions || [];
};

/**
 * Gets filtered construction type options for FO-6 structures based on brand-configured allowed types
 * @param {string} state - The state code (e.g., 'IL', 'MO') to get brand-specific allowed types
 * @returns {Array} - Array of allowed construction type options
 */
export const getFO6ConstructionTypeOptions = (state) => {
  const allOptions = lookupsJson.farm.agriculturalConstructionType || [];

  if (!state) {
    return allOptions; // Return all options when state not yet populated
  }

  const farmRiskBrandConfig = appConfig.farm?.[state]?.risks[FarmRiskType.Fo6Structures];
  const allowedTypes = farmRiskBrandConfig?.allowedOptions?.constructionTypes || DEFAULT_CONSTRUCTION_TYPES;

  return allOptions.filter((option) => allowedTypes.includes(option.id));
};

/**
 * Gets the property types exempt from requiring construction type for a given risk type,
 * driven by brand configuration.
 * @param {string} state - The state code (e.g., 'IL', 'MO')
 * @param {string} riskType - The farm risk type (e.g., FarmRiskType.Fo3)
 * @returns {string[]} Array of property type codes exempt from construction type requirement
 */
export const getConstructionTypeExemptPropertyTypes = (state, riskType) => {
  return appConfig.farm?.[state]?.risks[riskType]?.allowedOptions?.constructionTypeExemptPropertyTypes || [];
};

/**
 * Structure Category (propertyType) options filtered by risk type.
 * FO-3 excludes Mobile Home ('MH') per business rules.
 * FO-4 is locked to 'FD' (Home or Rental Unit) only — no option to change.
 * GitHub Issue: #28395 (TICKET-010), #29021
 */
const propertyTypeOptions = lookupsJson.farm.propertyType;
export const propertyTypeOptionsByRiskType = {
  [FarmRiskType.Fo1]: propertyTypeOptions,
  [FarmRiskType.Fo2]: propertyTypeOptions,
  [FarmRiskType.Fo3]: propertyTypeOptions.filter((opt) => opt.id !== 'MH'),
  [FarmRiskType.Fo4]: propertyTypeOptions
    .filter((opt) => opt.id === 'FD')
    .map((opt) => ({ ...opt, value: 'Home or Rental Unit' }))
};

/**
 * Residence Type/Usage options filtered by risk type.
 * FO-1: Excludes Contents ('C')
 * FO-2/FO-3: Excludes Vacant ('V') and Contents ('C')
 * FO-4: Contents ('C') only
 * GitHub Issue: #28395 (TICKET-010), #29021
 */
const residenceUsageOptions = lookupsJson.farm.residenceUsage;
export const residenceUsageOptionsByRiskType = {
  [FarmRiskType.Fo1]: residenceUsageOptions.filter((opt) => opt.id !== 'C'),
  [FarmRiskType.Fo2]: residenceUsageOptions.filter((opt) => !['V', 'C'].includes(opt.id)),
  [FarmRiskType.Fo3]: residenceUsageOptions.filter((opt) => !['V', 'C'].includes(opt.id)),
  [FarmRiskType.Fo4]: residenceUsageOptions.filter((opt) => opt.id === 'C')
};

/**
 * Dwelling construction type options (shared by FO-1 through FO-3).
 * FO-4 adds Mobile Home ('MH') — see getDwellingConstructionTypeOptions().
 * GitHub Issue: #29021, #29180
 */
const dwellingConstructionTypeOptions = lookupsJson.farm.dwellingConstructionType;
const fo4ConstructionTypeOptions = [...dwellingConstructionTypeOptions, { id: 'MH', value: 'Mobile Home' }].sort(
  (a, b) => a.value.localeCompare(b.value)
);

/**
 * Get dwelling construction type options for a given risk type.
 * FO-4 includes Mobile Home; other dwelling risk types do not.
 * Tiny Home ('T') is only available for FarmHome (Z) and FarmRental (Y) — issue #30683.
 * @param {string} riskType - The farm risk type (FO1, FO2, FO3, FO4)
 * @param {string} [selectedOption] - The farm policy type (F, Z, or Y). When omitted, Tiny Home is hidden.
 * @returns {Array} Construction type options
 */
export const getDwellingConstructionTypeOptions = (riskType, selectedOption) => {
  const baseOptions = riskType === FarmRiskType.Fo4 ? fo4ConstructionTypeOptions : dwellingConstructionTypeOptions;
  const tinyHomeAllowed = selectedOption === policyType.FarmHome || selectedOption === policyType.FarmRental;
  return tinyHomeAllowed ? baseOptions : baseOptions.filter((opt) => opt.id !== 'T');
};

/**
 * Get Structure Category options for a given risk type.
 * @param {string} riskType - The farm risk type (FO1, FO2, FO3, etc.)
 * @returns {Array} Filtered property type options
 */
export const getPropertyTypeOptions = (riskType) => propertyTypeOptionsByRiskType[riskType] || propertyTypeOptions;

/**
 * Get Residence Type/Usage options for a given risk type.
 * @param {string} riskType - The farm risk type (FO1, FO2, FO3, etc.)
 * @returns {Array} Filtered residence usage options
 */
export const getResidenceUsageOptions = (riskType) =>
  residenceUsageOptionsByRiskType[riskType] || residenceUsageOptions;

/**
 * Height formula constant for grain bin calculations.
 * H = ceil(GRAIN_BIN_HEIGHT_CONSTANT × (bushelCapacity / diameter²))
 * GitHub Issue: #29355
 */
const GRAIN_BIN_HEIGHT_CONSTANT = 1.467;

/**
 * Calculates the height of a grain bin from its diameter and bushel capacity.
 * Formula: H = ceil(1.467 × (BC / D²))
 * @param {number} diameter - The diameter of the grain bin in feet
 * @param {number} bushelCapacity - The bushel capacity
 * @returns {number} - The calculated height in feet, rounded up to nearest integer
 */
export const calculateGrainBinHeight = (diameter, bushelCapacity) => {
  if (!diameter || !bushelCapacity) {
    return 0;
  }
  return Math.ceil(GRAIN_BIN_HEIGHT_CONSTANT * (bushelCapacity / (diameter * diameter)));
};

// ─── Policy Type Mask Helpers ──────────────────────────────────────────────────
// These functions read the policyTypeMasks config from the brand config and return
// mask-aware values. Components call these instead of hardcoding behavior per type.
// The mask for F (full farm) is permissive, so all existing behavior is preserved.

/**
 * Returns the policy type mask for the given policyType and state.
 * Falls back to the F (full farm) mask if no mask is defined for the type.
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code (e.g., 'IL')
 * @returns {object|null} The mask object, or null if no masks are configured
 */
export const getFarmPolicyMask = (farmPolicyType, state) => {
  const masks = appConfig.farm?.[state]?.policyTypeMasks;
  if (!masks) {
    return null;
  }
  return masks[farmPolicyType] || masks[policyType.Farm] || null;
};

/**
 * Returns whether a farm policy type is currently live for offer creation.
 * Home (Z) and Rental (Y) are gated out of production until Forreston launches them
 * (#31043 strips their masks in forreston-production), so they are only live where their
 * own mask is present — getFarmPolicyMask can't be used here because it falls back to the
 * F mask. Full Farm (F) and any non-farm type are always live. When no state is given
 * (e.g. the new-offer default before an address is entered) we fall back to an
 * environment-level check across every configured farm state. (#31219)
 * @param {string} farmPolicyType - The policy type code (e.g. 'F', 'Z', 'Y')
 * @param {string} [state] - The state code (e.g. 'IL')
 * @returns {boolean}
 */
export const isFarmPolicyTypeLive = (farmPolicyType, state) => {
  if (farmPolicyType !== policyType.FarmHome && farmPolicyType !== policyType.FarmRental) {
    return true;
  }
  if (state) {
    return Boolean(appConfig.farm?.[state]?.policyTypeMasks?.[farmPolicyType]);
  }
  return Object.values(appConfig.farm ?? {}).some((stateConfig) => stateConfig?.policyTypeMasks?.[farmPolicyType]);
};

/**
 * Returns the farm policy type codes that are currently live, in dropdown order.
 * Used to drive the new-offer policy type default and required-selection behavior:
 * a single live type can keep auto-defaulting, multiple live types force a selection. (#31219)
 * @param {string} [state] - The state code (e.g. 'IL')
 * @returns {string[]} Subset of ['F','Z','Y']
 */
export const getLiveFarmPolicyTypes = (state) =>
  [policyType.Farm, policyType.FarmHome, policyType.FarmRental].filter((code) => isFarmPolicyTypeLive(code, state));

/**
 * Returns the filtered risk type options for the given policy type.
 * Uses the mask's allowedRiskTypes to restrict which risk types can be added.
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @param {Array} allRiskTypeOptions - The full set of risk type options from lookups
 * @returns {Array} Filtered risk type options
 */
export const getMaskedRiskTypeOptions = (farmPolicyType, state, allRiskTypeOptions) => {
  const mask = getFarmPolicyMask(farmPolicyType, state);
  const filtered = mask?.allowedRiskTypes
    ? allRiskTypeOptions.filter((opt) => mask.allowedRiskTypes.includes(opt.id))
    : allRiskTypeOptions;
  // Mask may also override the FO-6 Structures label (Rental Y / Home Z → "Other Structures (FO-6)"). (#31367)
  if (!mask?.fo6StructuresLabel) {
    return filtered;
  }
  return filtered.map((opt) =>
    opt.id === FarmRiskType.Fo6Structures ? { ...opt, value: mask.fo6StructuresLabel } : opt
  );
};

/**
 * Returns residence usage options filtered by both risk type (base filtering) and policy type mask.
 * The mask can further restrict which residence usages are available per risk type.
 * @param {string} riskType - The farm risk type (FO1, FO2, FO3, etc.)
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @returns {Array} Filtered residence usage options
 */
export const getMaskedResidenceUsageOptions = (riskType, farmPolicyType, state) => {
  const baseOptions = getResidenceUsageOptions(riskType);
  const mask = getFarmPolicyMask(farmPolicyType, state);
  if (!mask?.residenceUsageOverrides?.[riskType]) {
    return baseOptions;
  }
  const allowed = mask.residenceUsageOverrides[riskType];
  return baseOptions.filter((opt) => allowed.includes(opt.id));
};

/**
 * Returns FO6Structures building type (structure category) options filtered by the policy type mask.
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @returns {Array} Filtered structure category options
 */
export const getMaskedFO6StructureCategoryOptions = (farmPolicyType, state) => {
  const allCategories = state ? lookupsJson.farm.structureCategoriesByState[state] || [] : [];
  const mask = getFarmPolicyMask(farmPolicyType, state);
  if (!mask?.allowedFO6StructureCategories) {
    return allCategories;
  }
  return allCategories.filter((opt) => mask.allowedFO6StructureCategories.includes(opt.id));
};

/**
 * Returns the filtered liability type options for the given policy type mask.
 * The mask may also relabel options (Home/Rental drop the farm-flavored lookup labels,
 * e.g. 'Farm Personal Liability' → 'Personal Liability Coverage'). (#31695)
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @param {Array} allLiabilityTypeOptions - The full set of liability type options from lookups
 * @returns {Array} Filtered (and possibly relabeled) liability type options
 */
export const getMaskedLiabilityTypeOptions = (farmPolicyType, state, allLiabilityTypeOptions) => {
  const mask = getFarmPolicyMask(farmPolicyType, state);
  const filtered = mask?.allowedLiabilityTypes
    ? allLiabilityTypeOptions.filter((opt) => mask.allowedLiabilityTypes.includes(opt.id))
    : allLiabilityTypeOptions;
  if (!mask?.liabilityTypeLabels) {
    return filtered;
  }
  return filtered.map((opt) =>
    mask.liabilityTypeLabels[opt.id] ? { ...opt, value: mask.liabilityTypeLabels[opt.id] } : opt
  );
};

/**
 * Returns whether a specific liability coverage field should be hidden based on the mask.
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @param {string} liabilityType - The selected liability type ('P' or 'C')
 * @param {string} coverageName - The coverage field name (e.g., 'farmPollution', 'employersLiability')
 * @returns {boolean} True if the coverage should be hidden
 */
export const isLiabilityCoverageHiddenByMask = (farmPolicyType, state, liabilityType, coverageName) => {
  const mask = getFarmPolicyMask(farmPolicyType, state);
  if (!mask?.hiddenLiabilityCoverages?.[liabilityType]) {
    return false;
  }
  return mask.hiddenLiabilityCoverages[liabilityType].includes(coverageName);
};

/**
 * Returns whether a specific endorsement should be hidden based on the mask.
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @param {string} endorsementName - The endorsement name (e.g., 'farmExtraExpense', 'identityFraud')
 * @returns {boolean} True if the endorsement should be hidden
 */
export const isEndorsementHiddenByMask = (farmPolicyType, state, endorsementName) => {
  const mask = getFarmPolicyMask(farmPolicyType, state);
  if (!mask?.hiddenEndorsements) {
    return false;
  }
  return mask.hiddenEndorsements.includes(endorsementName);
};

/**
 * Returns the filtered inland marine type options based on the mask's hiddenInlandMarineTypes.
 * @param {string} farmPolicyType - The policy type code ('F', 'Z', or 'Y')
 * @param {string} state - The state code
 * @param {Array} allInlandMarineOptions - The full set of inland marine type options
 * @returns {Array} Filtered inland marine type options
 */
export const getMaskedInlandMarineTypeOptions = (farmPolicyType, state, allInlandMarineOptions) => {
  const mask = getFarmPolicyMask(farmPolicyType, state);
  if (!mask?.hiddenInlandMarineTypes) {
    return allInlandMarineOptions;
  }
  return allInlandMarineOptions.filter((opt) => !mask.hiddenInlandMarineTypes.includes(opt.id));
};

export const checkRiskTypeChange = ({ initialValues, currentValues, riskId }) => {
  const findRiskById = (farmLocations, targetRiskId) => {
    for (const location of farmLocations || []) {
      const risk = (location.risks || []).find((risk) => risk.id === targetRiskId);
      if (risk) {
        return risk;
      }
    }
    return null;
  };

  const initialRisk = findRiskById(initialValues?.farmLocations, riskId);
  const currentRisk = findRiskById(currentValues?.farmLocations, riskId);

  if (!initialRisk || !currentRisk) {
    return { riskTypeChanged: false, oldType: null, newType: null };
  }

  const riskTypeChanged = initialRisk.type !== currentRisk.type;

  return {
    riskTypeChanged,
    oldType: initialRisk.type,
    newType: currentRisk.type
  };
};
